<template>
  <div id="app">
    <user-input-listener />

    <mouse-custom-pointer />

    <global-navigation-top :class="'menu-first-level'" v-show="$route.name !== 'menu'" />

    <transition name="nav-fade">
      <global-navigation v-show="!excudedNavRoutes.includes($route.name)"/>
    </transition>

    <global-bottom-button />

    <transition
      :name="transitionName"
      @after-leave="$_afterLeave">
      <router-view />
    </transition>

    <cookie-law>
      <div slot="message">
        Here is my message for more info <router-link to="legal-notes">Click here</router-link>
      </div>
    </cookie-law>

  </div>
</template>

<script>
import { mapGetters, mapMutations } from 'vuex'
import UserInputListener from './components/UserInputListener'
import MouseCustomPointer from './components/MouseCustomPointer'
import GlobalNavigation from './components/GlobalNavigation'
import GlobalNavigationTop from './components/GlobalNavigationTop'
import GlobalBottomButton from './components/GlobalBottomButton'
import CookieLaw from './components/CookieLaw/CookieLaw.vue'

export default {
  components: {
    UserInputListener,
    MouseCustomPointer,
    GlobalNavigation,
    GlobalNavigationTop,
    GlobalBottomButton,
    CookieLaw
  },
  data: () => ({
    transitionName: 'default',
    excudedNavRoutes: [
      'loading',
      'landing',
      'contacts',
      'info-contacts',
      'job-contacts',
      'clients',
      'privacy',
      'cookie',
      'not-found',
      'article'
    ],
    overlayRoutes: [
      'menu',
      'contacts',
      'info-contacts',
      'job-contacts',
      'privacy',
      'cookie',
      'clients',
      'not-found',
      'courtesy',
      'article'
    ],
    transitions: {
      external: '',
      default: 'default',
      reverse: 'reverse',
      menu: 'menu',
      reverseMenu: 'menu-reverse',
      loading: 'loading'
    }
  }),
  computed: {
    ...mapGetters([
      'footerState'
    ])
  },
  methods: {
    ...mapMutations('animations', [
      'destroyPixiApps'
    ]),
    $_afterLeave () {
      // destory pixi apps on context switch
      this.destroyPixiApps()
    },
    $_getTransitionName (to, from) {
      if (from.name === null) return this.transitions.external

      // fake overlay animations
      if (this.overlayRoutes.includes(from.name)) return this.transitions.reverseMenu
      if (this.overlayRoutes.includes(to.name)) return this.transitions.menu

      if (from.name === 'loading') return this.transitions.loading

      // normal navigation
      const toPosition = to.meta.position
      const fromPosition = from.meta.position
      if (toPosition > fromPosition) return this.transitions.default
      return this.transitions.reverse
    }
  },
  watch: {
    '$route': {
      handler (to, from) {
        this.transitionName = this.$_getTransitionName(to, from)
      }
    }
  }
}
</script>

<style lang="scss">
@import url('https://fonts.googleapis.com/css?family=Raleway:300,400,600');
@import '/assets/scss/breakpoint.scss';
@import '/assets/scss/mixins.scss';

$violet: #0b3277;
$purple: #0487a3;
$white: #f2f2f2;

html {
  font-size: 62.5%;
  font-family: 'Raleway', sans-serif;
}

body{
  overflow-x: hidden;
}

*{
  font-weight: 400;
  line-height: 2;
  letter-spacing: .05rem;
}

strong{
  font-weight: 600;
}

figure img{
  display: block;
}

.single-view a:not(.no-mobile-underline-link):after, .accordion .rossetto-button--pdf span:after {
  @include media("<large"){
    transform: scaleX(1)!important;
  }
}
/* @end */

#app {
  min-width: 100%;
  min-height: 100vh;
  background-color: $purple;
  font-size: 2.0rem;
}

.grecaptcha-badge {
  display: none;
}

.content-wrapper{
  z-index: 11;
}

.single-view {
  width: 100%;
  min-height: 100vh;
  position: relative;
}

.nav-fade-enter, .nav-fade-leave-to {
  opacity: 0;
}

.nav-fade-enter-to, .nav-fade-leave {
  opacity: 1;
}

.nav-fade-enter-active {
  transition: opacity .5s 1.4s;
}

.nav-fade-leave-active {
  transition: opacity .5s;
}

.menu-enter-active, .menu-leave-active,
.menu-reverse-enter-active, .menu-reverse-leave-active {
  transition: all 1s ease-in;
  position: absolute;
}

.default-enter-active, .reverse-enter-active {
  transition: all 1s cubic-bezier(0.480, 0.175, 0.335, 0.830);
  position: absolute;
}

.default-leave-active, .reverse-leave-active {
  transition: all 1.2s cubic-bezier(0.480, 0.175, 0.335, 0.830);
  position: absolute;
}

.default-enter, .reverse-leave-to {
  transform: translateX(100%) translateZ(0);
  opacity: 1;
}

.default-enter-to {
  transform: translateX(0%) translateZ(0);
  opacity: 1;
}

.default-leave, .reverse-enter-to, .reverse-leave {
  transform: translateX(0%);
  opacity: 1;
}

.default-leave-to,  {
  transform: translateX(-100%);
  opacity: 1;
}

.reverse-enter {
  transform: translateX(-100%);
  opacity: 1;
}

.menu-enter {
  opacity: 0;
}

.menu-enter-to {
  opacity: 1;
}

.menu-reverse-leave {
  opacity: 1;
}

.menu-reverse-leave-to {
  opacity: 0;
}

.menu-enter-active, .menu-reverse-leave-active{
  transition: opacity .8s;
}

.loading-enter {
  opacity: 0;
}

.loading-enter-to {
  opacity: 1;
}

.loading-enter-active, .loading-leave-active {
  transition: all 1s ease-in;
}

.loading-leave {
  opacity: 1;
}

.loading-leave-to {
  opacity: 0;
}

/* @group Base Layout & Typo */

.block--header {
  color: $violet;
  h2, h3{
    margin: 0;
  }
  h2 + h3{
    margin-top: 3rem;
  }
  h2{
    font-size: 4.25rem;
    line-height: 1.2;
    color: #0A2F74;
  }
  h3{
    font-size: 2.4rem;
    line-height: 1.5;
  }

  @include media(">=large"){
    h2{
      font-size: 7rem;
      line-height: 9rem;
      &.--medium{
        font-size: 5rem;
        line-height: 1.4;
      }
    }
    h3{
      font-size: 3.5rem;
    }
  }
  @include media(">=xlarge"){
    h2{
      font-size: 7rem;
      line-height: 12rem;
    }
    h3{
      font-size: 5rem;
      line-height: 1.3;
    }
  }
}

.istit__content__inner{

  p a{
    color: #d0d0d0;
    text-decoration: none;
    position: relative;
    border-bottom: 1px solid;
  }
}

.block--content__quote {
  h4 {
    line-height: 12rem;
    color: #f2f2f2;
    text-transform: uppercase;
    font-size: 1.6rem;
    font-weight: 600;
    margin: 0;
  }
}

.block--content{
  h4 {
    a {
      color: #0487a3;
    }
  }

  ul {
    @include slashedList();

    li p {
      margin: 0;
      width: calc(100% - 20px);
    }
  }

  a[href$=".pdf"] {
    &::before {
      content: "";
      background-image: url("data:image/svg+xml,%3Csvg width='25px' height='25px' viewBox='0 0 25 25' version='1.1' xmlns='http://www.w3.org/2000/svg' xmlns:xlink='http://www.w3.org/1999/xlink'%3E%3Cpath id='pdfIco' fill='%23e30045' stroke-width='0' transform='translate%28-22.000000, -17.000000%29' d='M45.5961233,34.1 L45.5136218,34.1 C45.3936195,34.12 45.266117,34.14 45.1286144,34.14 C44.1985967,34.14 42.7685694,33.7525 41.2985413,33.11 C41.4810448,32.9075 41.6210475,32.9075 41.7635502,32.9075 C44.5861041,32.9075 45.3961195,33.41 45.5961233,33.6125 C45.6986253,33.6925 45.7386261,33.77 45.7761268,33.8325 C45.7561264,34.0325 45.6986253,34.06 45.5961233,34.1 M30.5333359,34.2175 L30.3083316,34.295 C30.4683346,33.69 30.9158432,32.9675 31.3183508,32.2 C32.2258682,30.4475 32.8958809,28.8575 33.3583898,27.3675 C34.1859056,29.18 35.2309255,30.67 36.4409486,31.6975 C36.6834532,31.92 36.9684587,32.14 37.2709645,32.3425 C35.515931,32.665 33.0933847,33.23 30.5333359,34.2175 M33.0508839,18.495 C33.0933847,18.415 33.1533859,18.3125 33.1933866,18.25 L33.4958924,18.3525 C33.8383989,18.8375 33.8783997,19.42 33.6758958,20.57 L33.6183947,20.8925 C33.4983924,21.335 33.4383913,21.9025 33.2958886,22.5475 L33.1933866,22.625 C32.7308778,20.8125 32.7083774,19.3425 33.0508839,18.495 M23.7007055,40.585 C23.8007074,39.8175 24.7057246,38.49 26.138252,37.18 C26.3382558,37.02 26.6807623,36.7975 27.1057704,36.555 C25.7932454,38.955 24.6082228,40.2825 23.7007055,40.585 M46.7436452,32.605 C46.1611341,31.6575 44.6486053,31.215 42.1485575,31.215 C41.3235418,31.215 40.4510251,31.255 39.5460079,31.355 C38.9009956,30.9925 38.3184844,30.5925 37.7934744,30.19 C36.5234502,29.14 35.3734282,27.305 34.5659128,25.0275 C34.5859132,24.9675 34.6084136,24.865 34.6484144,24.745 C35.3134271,22.065 35.935939,18.82 34.6059136,17.4875 C34.2834074,17.1675 33.8383989,17 33.3733901,17 L32.8908809,17 C32.1458666,17 31.4808539,17.7875 31.2983505,18.5525 C30.5533362,21.235 30.9933446,22.705 31.7383589,25.145 L31.7383589,25.17 C31.2333492,26.9425 30.593337,28.995 29.5658174,31.0725 C28.8783043,32.4225 28.2307919,33.6525 27.6282804,34.7 C26.820765,35.0825 26.2182535,35.405 25.8357462,35.6875 C23.4157,37.2 22.263178,38.8975 22.0406738,39.965 C21.9606722,40.3475 22.000673,40.69 22.1431757,41.0525 L22.208177,41.155 L23.1731954,41.775 C23.4157,41.9175 23.7407062,42 24.0632124,42 C25.6957435,42 27.5482789,40.085 30.0483266,35.8075 C30.1908293,35.7475 30.3108316,35.7125 30.4108335,35.6725 C32.4858731,35.0075 35.0684224,34.5375 38.5359886,34.1525 C40.5510271,35.1825 43.0560749,35.6475 44.5911042,35.6475 C45.476121,35.6475 46.0761325,35.43 46.4211391,35.0475 C46.7861461,34.685 46.9636494,34.2175 46.9836498,33.715 C47.0411509,33.3125 46.9436491,32.9275 46.7436452,32.605'%3E%3C/path%3E%3C/svg%3E");
      width: 25px;
      height: 25px;
      display: inline-block;
      vertical-align: middle;
      margin: 0 1rem 0 0;
      background-repeat: no-repeat;
    }
  }

  color: $violet;

  p a:not(.rossetto-button ){
    color: $purple;
    text-decoration: none;
    display: inline-block;
    position: relative;
    &:after{
      display: block;
      content: "";
      position: absolute;
      bottom: 0;
      width: 100%;
      height: 1px;
      background: $purple;
      transform: scaleX(0);
      transform-origin: right;
      transition: transform .3s ease-out;
    }
    &:hover{
      &:after{
        transform: scaleX(1);
        transform-origin: left;
      }
    }
  }
  /*& > h3, & > p{
    margin: 0;
  }*/
  & > h3{
    margin-bottom: 3rem;
    font-size: 1.6rem;
    text-transform: uppercase;
    font-weight: 600;
  }
  & > h4{
    line-height: 4.8rem;
    font-size: 2rem;
  }
  & > p, & > ul li{
    line-height: 2;
    font-size: 1.6rem;
  }
  .block--content__quote{
    background: $violet;
  }
  .block--content__quote p{
    line-height: 2;
    font-size: 2rem;
  }
  & > .block--content__quote p{
    margin-bottom: 0;
    color: $white;
  }
  @include media(">=large"){
    & > p, & > ul li{
      font-size: 1.8rem;
    }
    & > h4,  & > .block--content__quote p{
      font-size: 2.8rem;
    }
  }
  & > figure{
    margin: 0
  }
  & > ul.list--dashed{
    @include slashedList();
    span{
      line-height: 1.7;
    }
  }
  @include media(">=xlarge"){
    & > h4,  & > .block--content__quote p{
      font-size: 3.1rem;
    }
    & > h4{
      margin-bottom: 7rem;
    }
  }
}
.block--list{
  color: $violet;
  li > h3{
    font-size: 1.8rem;
    text-transform: uppercase;
  }
  li > p{
    line-height: 1.8;
    font-size: 1.6rem;
  }
  @include media(">=large"){
    li > p{
      font-size: 2rem;
      line-height: 1.6;
    }
  }
  @include media(">=xlarge"){
    li > p{
      font-size: 2.4rem;
      line-height: 1.8;
    }
  }
  & > ul{
    margin: 0;
    padding: 0;
  }
  & > figure{
    margin: 0;
  }
}
.block--schedule{
  color: $violet;
  h4{
    line-height: 1.4;
    font-size: 2rem;
  }
  li > p{
    line-height: 1.6;
    font-size: 1.6rem;
  }
  ul{
    margin: 0;
    padding: 0;
    list-style: none;
  }
  @include media(">=large"){
    h4{
      font-size: 2.8rem;
    }
  }
}

/* @end */

</style>
